/**
 * Operational status values for a store as stored in Firestore at stores/{storeId}.status.
 * Must stay aligned with GallixRules `isStoreDisabled` (status == 'DISABLED').
 */
export const StoreOperationalStatus = {
  Active: 'ACTIVE',
  Disabled: 'DISABLED',
} as const;

export type TStoreOperationalStatus = (typeof StoreOperationalStatus)[keyof typeof StoreOperationalStatus];

//#region INTERFACE IStore
/**
 * ### IStore
 * Interface representing a store with identifying information and user access metadata.
 *
 * @interface IStore
 * @extends Object
 * @defaults {@link DEFAULT_STORE}
 * @description This interface is used to represent store metadata within the application,
 *              including identifiers, administrative privileges, and user access levels.
 *              It is commonly used in access control, store listings, and filtering operations.
 *
 * @example
 * const example: IStore = {
 *   name: "My Store",
 *   storeId: "store_001",
 *   isAdminForStore: true,
 *   sequence: 1,
 *   userAccess: 3
 * };
 */
export interface IStore extends Object {
  /**
   * The name of the store.
   * @description Human-readable name for display purposes.
   *
   * @type {string}
   * @example "My Store"
   */
  name: string;

  /**
   * The unique identifier for the store.
   * @description Used as a key when querying or referencing a specific store.
   *
   * @type {string}
   * @example "store_001"
   */
  storeId: string;

  /**
   * Indicates whether the user has administrative access to the store.
   * @description If `true`, the user has full control over the store's operations and settings.
   *
   * @type {boolean}
   * @optional
   * @example true
   */
  isAdminForStore?: boolean;

  /**
   * Sequence number for ordering stores visually or logically.
   * @description Lower numbers may appear first in ordered lists or UIs.
   *
   * @type {number}
   * @optional
   * @example 1
   */
  sequence?: number;

  /**
   * Operational status from Firestore (`ACTIVE` / `DISABLED`).
   * Disabled stores are denied by security rules for most collection reads.
   *
   * @type {TStoreOperationalStatus}
   * @optional
   */
  status?: TStoreOperationalStatus;
  
  /**
   * Numeric code representing the user’s access level to the store.
   * @description Higher numbers may indicate elevated permissions or roles.
   *
   * @type {number}
   * @optional
   * @example 3
   */
  userAccess?: number;
}

/**
 * Returns true when the store is marked DISABLED in Firestore.
 */
export const isStoreOperationallyDisabled = (store: IStore | undefined | null): boolean =>
  store?.status === StoreOperationalStatus.Disabled;

/**
 * Tooltip shown on disabled stores in the store selector.
 */
export const DISABLED_STORE_SELECTOR_TOOLTIP =
  'Gallix Support has disabled this store.';

//#endregion

//#region CONST DEFAULT_STORE
/**
 * ### DEFAULT_STORE
 * A default store object used as a fallback or placeholder.
 *
 * @constant
 * @type {IStore}
 * @example
 * ```ts
 * const store = DEFAULT_STORE;
 * console.info(store.name); // "Unknown"
 * ```
 */
export const DEFAULT_STORE: IStore = {
  name: 'Techodactyl System',
  storeId: 'system',
  isAdminForStore: false,
  sequence: -1,
  userAccess: 0,
};
//#endregion
